import ApiFactory from '../../factories/ApiFactory';
import { serialize, prepAutocompleteData } from '@/services/UtilityService';
import moment from 'moment';
import { validate as uuidValidate } from 'uuid';
import {
	checkHasPermission,
	POS_BUSINESS_EXTRA_DELIVERY_TIME,
	EDIT_ORDER_REFUND_PERM,
	POS_EDIT_BUSINESS_MENU,
	POS_EDIT_BUSINESS_MENU_ITEM_PRICE,
	POS_EDIT_BUSINESS_TOPPINGS,
	SALES_EDIT_BUSINESS_OPENING_TIME_PERM,
	SALES_EDIT_BUSINESS_PERM,
} from '@/middlewares/permissions';

const list = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`systemadmin/v1/restaurants?${query}`).then(
			({ data }) => {
				resolve(data.data);
			},
			(error) => reject(error)
		);
	});
};

const autocomplete = (q, labelProp = 'restaurantName', valueProp = 'restaurantID') => {
	return new Promise((resolve) => {
		list(q).then((data) => {
			resolve(prepAutocompleteData(data, labelProp, valueProp));
		});
	});
};

const getReviews = (id) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`justeat/v2/restaurants/${id}/rates`).then(
			({ data }) => {
				const reviews = data.data['rates'].sort(
					(a, b) => moment(b['registrationtime']) - moment(a['registrationtime'])
				);
				const statistics = {
					rate1: reviews.filter((r) => r.count === 1).length,
					rate2: reviews.filter((r) => r.count === 2).length,
					rate3: reviews.filter((r) => r.count === 3).length,
					rate4: reviews.filter((r) => r.count === 4).length,
					rate5: reviews.filter((r) => r.count === 5).length,
				};
				resolve({ reviews, statistics });
			},
			(error) => reject(error)
		);
	});
};

const getProducts = (id, customerId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`businesses/v1/businesses/${id}/menu_items?status=1&&customerId=${customerId}`).then(
			({ data }) => {
				resolve(data.data);
			},
			reject
		);
	});
};

const getTransportCost = (id, filters) => {
	const query = serialize(filters);
	return new Promise((resolve, reject) => {
		ApiFactory.get(`justeat/v2/restaurants/${id}/zone?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getCustomItem = (id) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`justeat/v2/restaurants/${id}/menuitems/customitem`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getBusinessDetails = (id) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/businesses/${id}/full`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getBusinessStatus = (id, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/businesses/${id}/${status}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const changeBusinessStatus = (id, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`/posweb/v1/businesses/${id}/status/${status}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getDefaultHolidays = (year = moment().year()) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posweb/v1/businesses/opening_hours/holidays/years/${year}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createBusinessHours = (id, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`posweb/v1/businesses/${id}/opening_hours`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateBusinessHours = (id, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posweb/v1/businesses/${id}/opening_hours`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getPriceRanges = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`businesses/v1/price_ranges`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'value', 'id'));
		}, reject);
	});
};

const getContactTypes = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`businesses/v1/contact_types`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'description', 'id'));
		}, reject);
	});
};

const getBusinessTypes = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`businesses/v1/types`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'description', 'id'));
		}, reject);
	});
};

const createOrUpdateContact = (businessId, data) => {
	return new Promise((resolve, reject) => {
		let promise;
		const isNewContact = uuidValidate(data.id);
		if (!isNewContact) {
			promise = ApiFactory.put(`businesses/v1/businesses/${businessId}/store_details/contacts/${data.id}`, data);
		} else {
			const payload = { ...data };
			delete payload.id;
			promise = ApiFactory.post(`businesses/v1/businesses/${businessId}/store_details/contacts`, payload);
		}
		promise.then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createOrUpdateContract = (businessId, data) => {
	return new Promise((resolve, reject) => {
		let promise;
		if (data.id > 0) {
			promise = ApiFactory.put(`businesses/v1/businesses/${businessId}/contracts/${data.id}`, data);
		} else {
			promise = ApiFactory.post(`businesses/v1/businesses/${businessId}/contracts`, data);
		}
		promise.then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const deleteBusinessContact = (businessId, contactId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.delete(`businesses/v1/businesses/${businessId}/store_details/contacts/${contactId}`).then(() => {
			resolve();
		}, reject);
	});
};

const createOrUpdateStoreDetails = (data) => {
	return new Promise((resolve, reject) => {
		let promise;
		if (data.restaurantID) {
			promise = ApiFactory.put(`businesses/v1/businesses/${data.restaurantID}/store_details`, data);
		} else {
			promise = ApiFactory.post(`businesses/v1/businesses/store_details`, data);
		}
		promise.then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createBusinessMenu = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`businesses/v1/businesses/${businessId}/menu_status`, payload).then(() => {
			resolve();
		}, reject);
	});
};

const updateBusinessMenu = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`businesses/v1/businesses/${businessId}/menu_status`, payload).then(() => {
			resolve();
		}, reject);
	});
};

const createSEO = (businessId, data) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`businesses/v1/businesses/${businessId}/seo`, data).then(() => {
			resolve();
		}, reject);
	});
};

const updateSEO = (businessId, data) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`businesses/v1/businesses/${businessId}/seo`, data).then(() => {
			resolve();
		}, reject);
	});
};

const getUpdatedStatus = (businessId, data) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`businesses/v1/businesses/${businessId}/status`, data).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getBusinessZones = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/zones`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'description', 'id'));
		}, reject);
	});
};
const goLive = (businessId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`businesses/v1/businesses/${businessId}/online`).then(resolve, reject);
	});
};

const goOffline = (businessId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`businesses/v1/businesses/${businessId}/offline`).then(resolve, reject);
	});
};

const getZoneTemplates = (businessId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/businesses/${businessId}/service_zones`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createZones = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`/businesses/v1/businesses/${businessId}/service_zones`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateZones = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`/businesses/v1/businesses/${businessId}/service_zones`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createFeatures = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`/businesses/v1/businesses/${businessId}/features`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateFeatures = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`/businesses/v1/businesses/${businessId}/features`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const takeAwayPoints = (query) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/${query}/take-away-points`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'pointName', 'id'));
		}, reject);
	});
};

const createTakeAwayPoint = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`/businesses/v1/${businessId}/take-away-points`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const logs = (businessId, filters) => {
	const query = serialize(filters);
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/businesses/${businessId}/logs?${query}`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'pointName', 'id'));
		}, reject);
	});
};

const getToppings = (businessId, filters) => {
	const query = serialize(filters);
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/businesses/${businessId}/menu_items/topping_types?${query}`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'displayedText', 'id'));
		}, reject);
	});
};

const createTopping = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`/posweb/v1/businesses/${businessId}/menu_items/topping_types`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateTopping = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`/posweb/v1/businesses/${businessId}/menu_items/topping_types/${payload.id}`, payload).then(
			({ data }) => {
				resolve(data.data);
			},
			reject
		);
	});
};

const getToppingItems = (businessId, toppingId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/businesses/${businessId}/menu_items/topping_types/${toppingId}`).then(
			({ data }) => {
				resolve(data.data);
			},
			reject
		);
	});
};

const createToppingItem = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(
			`/posweb/v1/businesses/${businessId}/menu_items/topping_types/${payload.toppingTypeId}/items`,
			payload
		).then(() => {
			resolve();
		}, reject);
	});
};

const updateToppingItem = (businessId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(
			`/posweb/v1/businesses/${businessId}/menu_items/topping_types/${payload.toppingTypeId}/items/${payload.id}`,
			payload
		).then(() => {
			resolve();
		}, reject);
	});
};

const updateToppingItemStatus = (businessId, toppingId, itemId, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(
			`/posweb/v1/businesses/${businessId}/menu_items/topping_types/${toppingId}/items/${itemId}/active/${status}`
		).then(() => {
			resolve();
		}, reject);
	});
};

const getPercentages = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`businesses/v1/percentages`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getAvailableBusinessStatuses = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/businesses/v1/businesses/statuses`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'value', 'id'));
		}, reject);
	});
};

const updateBusinessStatus = (id, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`/businesses/v1/businesses/${id}/status/${status}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getBusinessClosingReasons = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posweb/v1/business_closing_reasons`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'value', 'id'));
		}, reject);
	});
};

const getBusinessRejectReasons = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posweb/v1/reject-reasons`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'description', 'id'));
		}, reject);
	});
};

const setBusinessOffline = (businessId, cityId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`posweb/v1/businesses/${businessId}/city/${cityId}/close`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const setBusinessOnline = (businessId, cityId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posweb/v1/businesses/${businessId}/city/${cityId}/closing_time/status`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const canEditBusiness = () => {
	return checkHasPermission([SALES_EDIT_BUSINESS_PERM]);
};

const canEditExtraDeliveryTime = () => {
	return checkHasPermission([POS_BUSINESS_EXTRA_DELIVERY_TIME]);
};

const canEditOpeningTimeBusiness = () => {
	return checkHasPermission([SALES_EDIT_BUSINESS_OPENING_TIME_PERM]);
};

const canEditBusinessMenu = () => {
	return checkHasPermission([POS_EDIT_BUSINESS_MENU]);
};

const canRefund = () => {
	return checkHasPermission([EDIT_ORDER_REFUND_PERM]);
};

const canEditMenuItemPrice = () => {
	return checkHasPermission([POS_EDIT_BUSINESS_MENU_ITEM_PRICE]);
};

const canEditToppings = () => {
	return checkHasPermission([POS_EDIT_BUSINESS_TOPPINGS]);
};

const getPaymentMethodsOptions = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`businesses/v1/payment_methods`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

export default {
	list,
	autocomplete,
	getReviews,
	getProducts,
	getTransportCost,
	getCustomItem,
	getBusinessDetails,
	getBusinessStatus,
	changeBusinessStatus,
	getDefaultHolidays,
	createBusinessHours,
	updateBusinessHours,
	getPriceRanges,
	getContactTypes,
	getBusinessTypes,
	createOrUpdateContact,
	createOrUpdateContract,
	deleteBusinessContact,
	createOrUpdateStoreDetails,
	createBusinessMenu,
	updateBusinessMenu,
	createZones,
	updateZones,
	createFeatures,
	updateFeatures,
	createSEO,
	updateSEO,
	getUpdatedStatus,
	getBusinessZones,
	goLive,
	goOffline,
	getZoneTemplates,
	takeAwayPoints,
	createTakeAwayPoint,
	logs,
	getToppings,
	createTopping,
	updateTopping,
	getToppingItems,
	createToppingItem,
	updateToppingItem,
	updateToppingItemStatus,
	getPercentages,
	getAvailableBusinessStatuses,
	updateBusinessStatus,
	canEditBusiness,
	canEditExtraDeliveryTime,
	canEditOpeningTimeBusiness,
	canEditBusinessMenu,
	canRefund,
	canEditMenuItemPrice,
	canEditToppings,
	getPaymentMethodsOptions,
	getBusinessClosingReasons,
	getBusinessRejectReasons,
	setBusinessOffline,
	setBusinessOnline,
};
